import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { Divider, ThemeProvider } from '@mui/material';
import { displayErrorMessage } from 'utils/tools_functions';
import JWTService from 'services/JWT';
import UserService from 'services/API/User';
import useAuth from 'providers/Auth/useAuth';
import { useTranslation } from 'react-i18next';

import InputLabel from 'components/generics/InputLabel';
import ssoIcon from 'assets/images/sso-icon.svg';
import SSOProviderModal from './SSOProviderModal';

import { Button as MButton } from '@mui/material';
import { customInvertedButtonTheme } from 'assets/styles/themes/generic_button_mui';

const DEFAULT_FORM = { username: '', password: '' };

const LoginForm = () => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [ssoModalOpen, setSsoModalOpen] = useState(false);
  const { setUser } = useAuth();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const onSubmit = (e) => {
    if (e) e.preventDefault();

    if (!formData.username?.trim() || !formData.password) {
      toast.error(t('errors.ERR_LOGIN_REQUIRED'));
      return;
    }

    setLoading(true);
    JWTService.signInWithCredentials(formData.username, formData.password, () => setLoading(false))
      .then((response) => {
        if (response.data?.['2fa_token']) {
          navigate(`/2fa?2fa_token=${encodeURIComponent(response.data['2fa_token'])}`);
          setLoading(false);
          return;
        }
        setLoading(false);
        toast.success(t('success.loggedIn'));
        const user = response.data.data;
        localStorage.setItem('cooperative', user.cooperative.id);
        setUser(user);
      })
      .catch((err) => {
        setLoading(false);
        if (err.status && err.status === 401) {
          if (err?.data?.error_type === 'COOPERATIVE_SUSPENDED') {
            toast.error(displayErrorMessage('ERR_COOPERATIVE_SUSPENDED'));
          } else {
            toast.error(displayErrorMessage('ERR_INCORRECT_CREDENTIAL'));
          }
          updateForm('password', '');
        } else {
          toast.error(displayErrorMessage('ERR_UNEXPECTED'));
          setFormData(DEFAULT_FORM);
        }
      });
  };

  const updateForm = (key, value) => setFormData({ ...formData, [key]: value });

  const handleSSOClick = () => {
    setSsoModalOpen(true);
  };

  const handleProviderSelect = async (identifier) => {
    setSsoModalOpen(false);
    const result = await UserService.initSso(identifier);
    if (!result.success) {
      toast.error(t('errors.ERR_SSO_FAILED'));
    }
  };

  return (
    <div>
      <form onSubmit={onSubmit}>
        <InputLabel
          name="username"
          value={formData.username}
          onChange={(e) => {
            updateForm('username', e);
          }}
          placeholder={t('form.username')}
          label={t('form.usernameLabel')}
          defaultStyle="login"
          disabled={loading}
        />
        <InputLabel
          name="password"
          value={formData.password}
          type="password"
          onChange={(e) => {
            updateForm('password', e);
          }}
          placeholder={t('form.password')}
          defaultStyle="login"
          label={t('form.passwordLabel')}
          disabled={loading}
        />
        <Link onClick={() => localStorage.setItem('action_password', 'reset')} to="/forgotPassword">
          {t('form.forgotPassword')}
        </Link>
        <ThemeProvider theme={customInvertedButtonTheme}>
          <MButton className="d-block" type="submit" disabled={loading}>
            {t('button.login')}
          </MButton>
        </ThemeProvider>
      </form>
      <Divider sx={{ my: 2 }} />
      <MButton className="link d-block" type="button" disabled={loading} onClick={handleSSOClick}>
        <div>
          {t('button.ssoLogin')} <img src={ssoIcon} alt="SSO" style={{ marginLeft: 8 }} />
        </div>
      </MButton>
      <SSOProviderModal
        open={ssoModalOpen}
        onClose={() => setSsoModalOpen(false)}
        onSelect={handleProviderSelect}
      />
    </div>
  );
};

export default LoginForm;
